import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingCart, Star } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext.js";
import { useAuth } from "../../contexts/AuthContext.js";
import { useFavorites } from "../../contexts/FavoritesContext.js";
import {
  showCartAdd,
  showError,
  showWishlistAdd,
  showWishlistRemove,
} from "../../utils/toastUtils.js";
import api from "../../api/client.js";
import "./ProductCard.css";
import logger from "../../utils/logger.js";

/* ── Star Rating Display ── */
function StarRatingDisplay({ value = 0 }) {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));

  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`star-btn ${star <= rating ? "filled" : ""}`}
        >
          <Star
            size={13}
            fill={star <= rating ? "#f59e0b" : "none"}
            stroke={star <= rating ? "#f59e0b" : "#d1d5db"}
            strokeWidth={1.5}
          />
        </span>
      ))}
      {value > 0 && <span className="rating-number">{rating.toFixed(1)}</span>}
    </div>
  );
}

/* ── Product Badge ── */
function ProductBadge({ type, text, discount }) {
  if (type === "discount" && discount > 0) {
    return <span className="badge discount">-{discount}%</span>;
  }
  if (type === "new") {
    return <span className="badge new">{text}</span>;
  }
  if (type === "bestseller") {
    return <span className="badge bestseller">{text}</span>;
  }
  return null;
}

/* ── Main ProductCard ── */
export default function ProductCard({ product, onRemoveFromFavorites }) {
  const { lang, t } = useLanguage();
  const { isLogged } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const imageRef = useRef(null);

  const isAr = lang === "ar";

  /* ── Derived Data ── */
  const isNew = product.created_at
    ? new Date(product.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    : !!product.is_new;

  const originalPriceCandidate = Number(
    product.original_price ?? product.old_price ?? product.price ?? 0
  );
  const currentPriceCandidate = Number(
    product.final_price ?? product.sale_price ?? product.price ?? 0
  );
  const hasDiscount =
    product.has_discount === true ||
    (originalPriceCandidate > 0 &&
      currentPriceCandidate > 0 &&
      currentPriceCandidate < originalPriceCandidate);
  const discount = hasDiscount
    ? Math.round(
        ((originalPriceCandidate - currentPriceCandidate) / originalPriceCandidate) * 100
      )
    : null;
  const showDiscount = discount > 0;

  const isBestseller = !!product.is_bestseller;

  const name = isAr
    ? product.name_ar || product.name_en || t("product_no_name", "بدون اسم")
    : product.name_en || product.name_ar || t("product_no_name", "No name");

  const rawPrice = currentPriceCandidate > 0 ? currentPriceCandidate : 0;
  const hasValidPrice = rawPrice > 0;
  const originalPrice =
    hasDiscount && originalPriceCandidate > 0 ? originalPriceCandidate : null;

  const imageSrc =
    product.image_url ||
    (product.image && /^https?:\/\//i.test(product.image) ? product.image : "");

  const isLiked = isFavorite(product.id);
  const averageRating = Number(product?.average_rating || 0);
  const reviewsCount = Number(product?.reviews_count || 0);

  /* ── Cart Fly Animation ── */
  const flyToCart = () => {
    if (!imageRef.current) return;

    const imgElement = imageRef.current;
    const startRect = imgElement.getBoundingClientRect();

    let cartTarget =
      document.getElementById("cart-icon-target") ||
      document.querySelector("[data-cart-icon]") ||
      document.querySelector(".cart-icon") ||
      document.querySelector(".header-cart") ||
      document.querySelector("[aria-label*='cart' i]") ||
      document.querySelector("[aria-label*='سلة' i]") ||
      document.querySelector("header [class*='cart']") ||
      document.querySelector("nav [class*='cart']");

    const viewportWidth = window.innerWidth;

    let targetX, targetY;
    if (cartTarget) {
      const r = cartTarget.getBoundingClientRect();
      targetX = r.left + r.width / 2;
      targetY = r.top + r.height / 2;
    } else {
      targetX = isAr ? 50 : viewportWidth - 50;
      targetY = 50;
    }

    flyToPosition(imgElement, startRect, targetX, targetY);
  };

  const flyToPosition = (imgElement, startRect, targetX, targetY) => {
    const startX = startRect.left + startRect.width / 2;
    const startY = startRect.top + startRect.height / 2;

    const dx = targetX - startX;
    const dy = targetY - startY;
    const curveX = dy * 0.3;
    const curveY = dx * -0.3 - 40;

    const flyer = document.createElement("div");
    flyer.className = "flying-product";

    const flyerImg = document.createElement("img");
    flyerImg.src = imageSrc || "/placeholder-product.png";
    flyerImg.alt = name;
    flyer.appendChild(flyerImg);
    document.body.appendChild(flyer);

    const animation = flyer.animate(
      [
        {
          transform: `translate(${startX - 24}px, ${startY - 24}px) scale(1)`,
          opacity: 0.9,
        },
        {
          transform: `translate(${startX + dx * 0.5 + curveX - 24}px, ${startY + dy * 0.5 + curveY - 24}px) scale(0.65)`,
          opacity: 0.75,
          offset: 0.5,
        },
        {
          transform: `translate(${targetX - 24}px, ${targetY - 24}px) scale(0.1)`,
          opacity: 0,
        },
      ],
      { duration: 750, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }
    );

    animation.onfinish = () => {
      flyer.remove();
      const cartEl = document.getElementById("cart-icon-target");
      if (cartEl) {
        const badge = cartEl.querySelector(".cart-badge");
        if (badge) {
          badge.classList.add("pulse");
          setTimeout(() => badge.classList.remove("pulse"), 500);
        }
      }
    };
  };

  /* ── Cart State Helpers ── */
  const updateCartCount = (newItem) => {
    const currentCart = JSON.parse(localStorage.getItem("cart") || "[]");
    const existingIdx = currentCart.findIndex(
      (item) => item.id === newItem.id && item.variant_id === newItem.variant_id
    );

    let updatedCart;
    if (existingIdx >= 0) {
      updatedCart = [...currentCart];
      updatedCart[existingIdx].quantity = (updatedCart[existingIdx].quantity || 1) + 1;
    } else {
      updatedCart = [...currentCart, { ...newItem, quantity: 1 }];
    }

    localStorage.setItem("cart", JSON.stringify(updatedCart));
    const total = updatedCart.reduce((s, i) => s + (i.quantity || 1), 0);
    window.dispatchEvent(
      new CustomEvent("cart-updated", {
        detail: { count: total, items: updatedCart, action: "add", productName: name },
      })
    );
    return total;
  };

  function getFirstVariantId() {
    if (Array.isArray(product?.variants) && product.variants.length > 0) {
      const first = product.variants[0];
      return first?.id ? Number(first.id) : null;
    }
    return null;
  }

  /* ── Event Handlers ── */
  const toggleFavourite = async () => {
    if (!isLogged) { navigate("/login"); return; }
    try {
      const wasLiked = isLiked;
      
      /* animation */
      if (wasLiked && onRemoveFromFavorites) {
        onRemoveFromFavorites(product.id);
      }
      
      /* toggleFavorite API + Context + Badge + Toast */
      const didUpdate = await toggleFavorite({ id: product.id, name, price: rawPrice, image: imageSrc });
      if (!didUpdate) return;
      
      /* Toast */
      wasLiked ? showWishlistRemove(name) : showWishlistAdd(name);
    } catch (error) {
      logger.error("Favourites error:", error);
    }
  };

  const handleAdd = async () => {
    if (!isLogged) { navigate("/login"); return; }
    try {
      const variantId = getFirstVariantId();
      if (!variantId) return;

      flyToCart();

      const res = await api.apiPost("cart/add", { variant_id: variantId, quantity: 1 });

      if (res?.message === "Added to cart" || res?.success === true) {
        updateCartCount({ id: product.id, variant_id: variantId, name, price: rawPrice, image: imageSrc });
        showCartAdd(name);
      } else {
        logger.warn("Cart add returned:", res?.message);
        showError(
          isAr ? "تعذر الإضافة للسلة" : "Unable to add to cart",
          res?.message || (isAr ? "هذا المنتج غير متاح للشراء حاليًا." : "This product is not available for purchase right now.")
        );
      }
    } catch (e) {
      logger.error("Add to cart error:", e);
      showError(
        isAr ? "تعذر الإضافة للسلة" : "Unable to add to cart",
        e?.response?.data?.message || e?.message || (isAr ? "هذا المنتج غير متاح للشراء حاليًا." : "This product is not available for purchase right now.")
      );
    }
  };

  /* ── Badges ── */
  const badges = [];
  if (showDiscount) {
    badges.push({ type: "discount", key: "discount", discount });
  }
  if (isNew) {
    badges.push({ type: "new", key: "new", text: isAr ? "جديد" : "NEW" });
  }
  if (isBestseller) {
    badges.push({ type: "bestseller", key: "bestseller", text: isAr ? "الأكثر مبيعاً" : "BESTSELLER" });
  }

  /* ── Render ── */
  return (
    <div
      className={`product-card ${isAr ? "rtl-card" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div className="product-image-wrap">
        {badges.length > 0 && (
          <div className="product-badges">
            {badges.map((badge) => (
              <ProductBadge key={badge.key} {...badge} />
            ))}
          </div>
        )}

        <Link to={`/product/${product.id}`} className="product-link">
          <div className="product-image">
            {imageSrc ? (
              <img ref={imageRef} src={imageSrc} alt={name} loading="lazy" />
            ) : (
              <div className="no-image">
                {t("no_image", isAr ? "لا توجد صورة" : "No image")}
              </div>
            )}
          </div>
        </Link>

        <button
          className={`heart-btn ${isLiked ? "liked" : ""}`}
          onClick={toggleFavourite}
          title={
            isLiked
              ? t("remove_from_favourites", isAr ? "إزالة من المفضلة" : "Remove from favourites")
              : t("add_to_favourites", isAr ? "إضافة للمفضلة" : "Add to favourites")
          }
        >
          <Heart size={18} />
        </button>

        <button
          className={`add-cart-btn desktop-only ${isHovered ? "show" : ""}`}
          onClick={handleAdd}
        >
          <ShoppingCart size={15} />
          <span>{t("btn_add_to_cart", isAr ? "أضف إلى السلة" : "Add to cart")}</span>
        </button>

        <button
          className="add-cart-btn mobile-only"
          onClick={handleAdd}
          aria-label={isAr ? "أضف إلى السلة" : "Add to cart"}
        >
          <ShoppingCart size={17} />
        </button>
      </div>

      <div className="product-details">
        <h3 className="product-name" title={name}>{name}</h3>

        <div className="product-rating">
          <StarRatingDisplay value={averageRating} />
          {reviewsCount > 0 && <span className="reviews-count">({reviewsCount})</span>}
        </div>

        {hasValidPrice ? (
          <div className="price-row">
            <span className="price-current">${rawPrice.toFixed(2)}</span>
            {originalPrice && <span className="price-old">${originalPrice.toFixed(2)}</span>}
          </div>
        ) : (
          <span className="no-price">
            {t("price_unavailable", isAr ? "السعر غير متوفر" : "Price unavailable")}
          </span>
        )}

        {product.colors && product.colors.length > 0 && (
          <div className="colors">
            {product.colors.slice(0, 4).map((color, i) => (
              <span key={i} className="color-dot" style={{ background: color }} />
            ))}
            {product.colors.length > 4 && (
              <span className="more-colors">+{product.colors.length - 4}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
